/**
 * TyCAdmin.jsx — Elite Vacances
 * Gestión de promociones TyC con API compartida (sin afectar GAC).
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getAllTycElite,
  createTycElite,
  updateTycElite,
  deleteTycElite,
} from "services/tycElite.service";
import {
  filterEliteTycRecords,
  buildTycElitePayload,
  getTycEliteErrorMessage,
} from "utils/tycElite";
import "../styles/TyCAdmin.css";
import "../styles/admin-theme.css";

const DEFAULT_PROMOS = [
  {
    apiId: null,
    id: "promo-vik-001",
    titulo: "Punta Cana - Hotel VIK Arena Blanca",
    texto: `Tarifa válida para dos personas, únicamente para uso en el Hotel VIK Arena Blanca, en el destino de Punta Cana, República Dominicana. Ciclo de viaje: tres (3) noches y cuatro (4) días. Incluye: Alimentación (desayuno, almuerzo, cena, botanas las 24 horas), Bebidas: bebidas alcohólicas nacionales y selectas marcas internacionales las 24 horas, jugos, cocteles y refrescos. El minibar y dispensador de licores en la habitación también están incluidos. Tarifa sujeta a disponibilidad hotelera: Disponibilidad habitacional 50 unidades. Tarifa válida para compras del 10 de abril de 2026 al 30 de junio de 2026. Válida para ciclo de viaje comprendido entre el 01 de mayo de 2026 al 23 de diciembre de 2026. No incluye: Tiquetes de transporte terrestre ni aéreos, traslados terrestres, asistencia médica. Tarifa exclusiva para afiliados a Elite Vacances SAS.`,
    activo: true,
  },
];

function newId() {
  return "promo-" + Date.now().toString(36);
}

function PromoRow({ promo, index, onChange, onRemove, onToggle }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`tyc-row ${!promo.activo ? "tyc-row--off" : ""}`}>
      <div className="tyc-row__head" onClick={() => setOpen((o) => !o)}>
        <div className="tyc-row__left">
          <span className="tyc-row__num">#{index + 1}</span>
          <span className="tyc-row__ttl">{promo.titulo || "Sin título"}</span>
          {!promo.activo && (
            <span className="tyc-badge tyc-badge--off">Inactivo</span>
          )}
        </div>
        <div className="tyc-row__actions" onClick={(e) => e.stopPropagation()}>
          <button
            className={`tyc-toggle ${promo.activo ? "tyc-toggle--on" : ""}`}
            onClick={() => onToggle(index)}
            title={promo.activo ? "Desactivar" : "Activar"}
          >
            {promo.activo ? "● Activo" : "○ Inactivo"}
          </button>
          <button className="tyc-remove" onClick={() => onRemove(index)}>
            ✕
          </button>
          <span className="tyc-chevron">{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div
          className="tyc-row__body"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tyc-field">
            <label>ID único</label>
            <input
              className="tyc-input tyc-input--sm"
              type="text"
              value={promo.id}
              onChange={(e) => onChange(index, "id", e.target.value)}
            />
            <small>Referencia interna. No uses espacios.</small>
          </div>

          <div className="tyc-field">
            <label>Título de la promoción</label>
            <input
              className="tyc-input"
              type="text"
              placeholder="Ej: Punta Cana - Hotel VIK Arena Blanca"
              value={promo.titulo}
              onChange={(e) => onChange(index, "titulo", e.target.value)}
            />
          </div>

          <div className="tyc-field">
            <label>
              Texto de términos y condiciones
              <span className="tyc-charcount">{promo.texto.length} caracteres</span>
            </label>
            <textarea
              className="tyc-textarea"
              rows={10}
              placeholder="Escribe los términos y condiciones completos..."
              value={promo.texto}
              onChange={(e) => onChange(index, "texto", e.target.value)}
            />
            <small>
              Puedes usar HTML básico: &lt;strong&gt;, &lt;br&gt;, &lt;ul&gt;&lt;li&gt;, etc.
            </small>
          </div>

          <div className="tyc-field">
            <label>Vista previa</label>
            <div
              className="tyc-preview"
              dangerouslySetInnerHTML={{ __html: promo.texto }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function TyCAdmin() {
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [removedApiIds, setRemovedApiIds] = useState([]);

  const refreshPromos = useCallback(async () => {
    try {
      const { data } = await getAllTycElite();
      const elitePromos = filterEliteTycRecords(Array.isArray(data) ? data : []);
      setPromos(elitePromos.length > 0 ? elitePromos : DEFAULT_PROMOS);
      setRemovedApiIds([]);
    } catch (error) {
      console.error(error);
      toast.error(
        getTycEliteErrorMessage(error, "No se pudieron cargar las promociones"),
      );
      setPromos(DEFAULT_PROMOS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshPromos();
  }, [refreshPromos]);

  const updatePromo = (i, key, val) =>
    setPromos((prev) =>
      prev.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)),
    );

  const togglePromo = (i) =>
    setPromos((prev) =>
      prev.map((p, idx) => (idx === i ? { ...p, activo: !p.activo } : p)),
    );

  const removePromo = (i) => {
    if (!window.confirm("¿Eliminar esta promoción?")) return;

    setPromos((prev) => {
      const promo = prev[i];
      if (promo?.apiId) {
        setRemovedApiIds((ids) => [...ids, promo.apiId]);
      }
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const addPromo = () =>
    setPromos((prev) => [
      ...prev,
      { apiId: null, id: newId(), titulo: "", texto: "", activo: true },
    ]);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const promo of promos) {
        const payload = buildTycElitePayload(promo);

        if (promo.apiId) {
          await updateTycElite(promo.apiId, payload);
        } else {
          await createTycElite(payload);
        }
      }

      for (const apiId of removedApiIds) {
        await deleteTycElite(apiId);
      }

      await refreshPromos();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success("Promociones guardadas correctamente");
    } catch (error) {
      console.error(error);
      toast.error(
        getTycEliteErrorMessage(error, "Error al guardar las promociones"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (
      window.confirm(
        "¿Restaurar las promociones originales? Los cambios no guardados se perderán.",
      )
    ) {
      setPromos(JSON.parse(JSON.stringify(DEFAULT_PROMOS)));
      setRemovedApiIds([]);
    }
  };

  return (
    <div className="tyc-wrap">
      <div className="tyc-shell">
        <header className="tyc-topbar">
          <button
            className="tyc-back"
            onClick={() => navigate("/8c4f9d17b6e245dca9814f7a5b6d3e2f")}
          >
            ← Volver
          </button>
          <div className="tyc-topbar__title">
            <span className="tyc-topbar__icon">📋</span>
            <div>
              <h1 className="tyc-topbar__h1">Términos y Condiciones</h1>
              <p className="tyc-topbar__sub">Promociones — Elite Vacances</p>
            </div>
          </div>
        </header>

        <div className="tyc-body">
      {saved && (
        <div className="tyc-notice tyc-notice--ok">
          ✓ Promociones guardadas correctamente.
        </div>
      )}

      <div className="tyc-hint">
        Cada promoción tiene un <strong>título</strong> y su bloque de{" "}
        <strong>términos y condiciones</strong>. Puedes activarlas o desactivarlas
        sin eliminarlas. Los cambios se guardan en el servidor y se ven en el sitio
        para todos los visitantes.
      </div>

      <div className="tyc-stats">
        <div className="tyc-stat">
          <span className="tyc-stat__num">{promos.length}</span>
          <span className="tyc-stat__lbl">Promociones</span>
        </div>
        <div className="tyc-stat">
          <span className="tyc-stat__num">
            {promos.filter((p) => p.activo).length}
          </span>
          <span className="tyc-stat__lbl">Activas</span>
        </div>
        <div className="tyc-stat">
          <span className="tyc-stat__num">
            {promos.filter((p) => !p.activo).length}
          </span>
          <span className="tyc-stat__lbl">Inactivas</span>
        </div>
      </div>

      {loading ? (
        <div className="tyc-empty">Cargando promociones…</div>
      ) : (
        <div className="tyc-list">
          {promos.length === 0 && (
            <div className="tyc-empty">
              No hay promociones. Agrega una con el botón de abajo.
            </div>
          )}
          {promos.map((p, i) => (
            <PromoRow
              key={p.apiId ?? `promo-row-${i}`}
              index={i}
              promo={p}
              onChange={updatePromo}
              onRemove={removePromo}
              onToggle={togglePromo}
            />
          ))}
        </div>
      )}

      <div className="tyc-add-wrap">
        <button className="tyc-btn tyc-btn--secondary" onClick={addPromo}>
          ＋ Agregar promoción
        </button>
      </div>

      <div className="tyc-save-bar">
        <button className="tyc-btn tyc-btn--ghost" onClick={handleReset}>
          ↺ Restaurar defaults
        </button>
        <button
          className="tyc-btn tyc-btn--primary tyc-btn--lg"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Guardando…" : "✓ Guardar cambios"}
        </button>
      </div>
        </div>
      </div>
    </div>
  );
}
